import React, { useState } from 'react';
import { Button, Input, Form, notification } from 'antd';
import axios from 'axios';
import { useAppData } from '../context/DataContext';
import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  AsYouType
} from 'libphonenumber-js';

const secrets = require('../secrets.js');
const api = secrets.server_url;

/**
 * Component for collecting a user's phone number with clear benefit messaging
 */
function PhonePromptContent({ onClose }) {
  const { userInfo, setAppData } = useAppData();
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form] = Form.useForm();

  // Default country for phone formatting
  const DEFAULT_COUNTRY = 'US';

  // Format phone number as user types
  const handlePhoneChange = (e) => {
    const formatter = new AsYouType(DEFAULT_COUNTRY);
    setPhone(formatter.input(e.target.value));
  };

  // Validate the phone number
  const isPhoneValid = (phoneNumber) => {
    try {
      return isValidPhoneNumber(phoneNumber, DEFAULT_COUNTRY);
    } catch (error) {
      return false;
    }
  };

  // Standardize phone number format
  const standardizePhoneNumber = (phoneNumber) => {
    try {
      const parsedNumber = parsePhoneNumberFromString(phoneNumber, DEFAULT_COUNTRY);
      if (parsedNumber && parsedNumber.isValid()) {
        return parsedNumber.format('E.164');
      }
    } catch (error) {
      console.error('Error standardizing phone number:', error);
    }
    return phoneNumber;
  };

  // Handle saving the phone number
  const handleSavePhone = async () => {
    if (!phone || !isPhoneValid(phone)) {
      setError('Please enter a valid phone number');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const standardizedPhone = standardizePhoneNumber(phone);
      
      // Get the current user ID from the userInfo
      if (!userInfo || !userInfo._id) {
        setError('User information not available. Please try again later.');
        setIsSubmitting(false);
        return;
      }
      
      // Update user profile with phone number using PUT to the user ID endpoint
      const response = await axios.put(
        `${api}user/${userInfo._id}`,
        { phone: standardizedPhone }, // Use 'phone' field instead of 'phoneNumber'
        {
          withCredentials: true,
          headers: {
            'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.data) {
        // Update the user info in the app context
        setAppData(prev => ({
          ...prev,
          userInfo: {
            ...prev.userInfo,
            phone: standardizedPhone // Use 'phone' field to match backend
          }
        }));

        notification.success({
          message: 'Phone number added',
          description: 'You\'ll now receive updates and reminders about your events!'
        });

        onClose();
      } else {
        setError('Failed to save your phone number. Please try again.');
      }
    } catch (error) {
      console.error('Error saving phone number:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <h3 style={{ marginBottom: '16px' }}>Stay Updated with Event Reminders</h3>
      <p style={{ marginBottom: '24px' }}>
        Add your phone number to receive text message reminders for events you're attending.
        We'll only send you important notifications about your upcoming events.
      </p>

      <Form form={form} layout="vertical" style={{ maxWidth: '300px', margin: '0 auto' }}>
        <Form.Item 
          label="Phone Number" 
          validateStatus={error ? 'error' : ''}
          help={error}
        >
          <Input
            placeholder="(123) 456-7890"
            value={phone}
            onChange={handlePhoneChange}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '20px' }}>
          <Button onClick={onClose}>
            Maybe Later
          </Button>
          <Button 
            type="primary" 
            onClick={handleSavePhone}
            loading={isSubmitting}
            disabled={!phone || !isPhoneValid(phone)}
          >
            Save Phone Number
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default PhonePromptContent; 